/**
 * Copyright (c) Platform Science Inc. All rights reserved.
 */

import axios, { AxiosResponse } from 'axios';
import moment from 'moment';
import { getItem, setItem } from '../../util/localStorage';
import {
  getIdToken, localStorageKeys, setAccessToken,
  setAccessTokenExpiration, setIdToken, setProviderHostUsedForAuth,
  getProviderHostUsedForAuth, clearProviderHostUsedForAuth,
} from './userSessionDetails';
import { FeatureFlag } from '../../featureFlags/featureFlags';
import { featureHubClientManager } from '../../featureFlags/featureHubClient';

function generateCodeVerifier(): string {
  let codeVerifier = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.-_~';
  const charactersLength = characters.length;
  for (let i = 0; i < 128; i++) {
    codeVerifier += (characters.charAt(Math.floor(Math.random() * charactersLength)));
  }
  return codeVerifier;
}

async function toPkceChallenge(codeVerifier: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const binaryString = String.fromCharCode.apply(null, new Uint8Array(hashBuffer));
  return btoa(binaryString).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

/**
 * Get the provider host based on PSID feature flag
 * Returns PSID host if feature flag is enabled, otherwise returns TID host (legacy)
 */
function getProviderHost(): string {
  const fhClient = featureHubClientManager.getClient();
  
  try {
    if (!fhClient) {
      return window.config.providerHost;
    }

    const repository = fhClient as any;
    if (repository.readynessState !== 'Ready') {
      return window.config.providerHost;
    }

    const isPSIDEnabled = fhClient.feature(FeatureFlag.KEYCLOAK_MIGRATION_ENABLED).enabled;
    return isPSIDEnabled ? window.config.providerHostPSID : window.config.providerHost;
  } catch (error) {
    return window.config.providerHost;
  }
}

export async function redirectToLogin() {
  let storeWindowLocation = window.location.search ? window.location.href.split('?')[0] : window.location.href;
  const regex = /&?identity_provider=[a-zA-Z_]*/g;
  const identityProvider = window.location.href.match(regex)?.[0].split('=')[1];
  const codeVerifier = generateCodeVerifier();
  
  if (!window.location.hash) {
    storeWindowLocation += '#/';
  }
  
  setItem(localStorageKeys.previousPageLocalStorageKey, storeWindowLocation);
  setItem(localStorageKeys.codeVerifierKey, codeVerifier);
  
  const providerHost = getProviderHost();
  setProviderHostUsedForAuth(providerHost);
  const isPSIDEnabled = providerHost === window.config.providerHostPSID;
  
  let redirectUri = window.config.redirectUri; 
  if (window.location.origin.includes('clearinghouse')) {
    redirectUri = window.config.redirectUriEldch;
  } else if (window.location.origin.includes('platformscience')) {
    redirectUri = window.config.redirectUriPlatformScience;
  }
  
  const clientId = isPSIDEnabled ? window.config.clientIdPSID : window.config.clientId;
  const codeChallenge = await toPkceChallenge(codeVerifier);
  const appName = window.config.appName;
  const scope = isPSIDEnabled ? 'openid' : `openid+${appName}`;

  let queryString = (`?scope=${scope}&response_type=code&redirect_uri=${redirectUri}` +
  `&client_id=${clientId}&code_challenge=${codeChallenge}&code_challenge_method=S256`);
  
  if (identityProvider) {
    const idpHintQueryParameter = isPSIDEnabled ? 'kc_idp_hint' : 'identity_provider';
    queryString += `&${idpHintQueryParameter}=${identityProvider}`;
  }
  
  const authUrl = `https://${providerHost}${isPSIDEnabled ? 'auth' : 'authorize'}${queryString}`;

  window.location.assign(authUrl);
}

/**
 * Get token using authorization code
 */
async function getTokenByAuthCode(code: string): Promise<AxiosResponse<any>> {
  const { redirectUri, redirectUriEldch, redirectUriPlatformScience, clientId, clientIdPSID } = window.config;
  const providerHost = getProviderHost();
  const isPSIDEnabled = providerHost === window.config.providerHostPSID;
  
  let redirectUriEld = redirectUri; 
  if (window.location.origin.includes('clearinghouse')) {
    redirectUriEld = redirectUriEldch;
  } else if (window.location.origin.includes('platformscience')) {
    redirectUriEld = redirectUriPlatformScience;
  }
  
  const codeVerifier = getItem(localStorageKeys.codeVerifierKey) || '';
  const selectedClientId = isPSIDEnabled ? clientIdPSID : clientId;
  
  const formData = new URLSearchParams();
  formData.append('grant_type', 'authorization_code');
  formData.append('code', code);
  if(providerHost === window.config.providerHost) {
    formData.append('tenantDomain', 'trimble.com');
  }
  formData.append('redirect_uri', decodeURIComponent(redirectUriEld));
  formData.append('client_id', selectedClientId);
  formData.append('code_verifier', codeVerifier);
  return axios.post(`https://${providerHost}token`, formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}

/**
 * Retrieve the arguments form the Url, and return it.
 */
export async function getAccessTokenFromUrl() {
  const params = new URLSearchParams(document.location.search.substring(1));
  const authCode = params.get('code');
  if (authCode) {
    const tokenResponse = await getTokenByAuthCode(authCode);

    const accessToken = tokenResponse.data.access_token;
    const idToken = tokenResponse.data.id_token;
    /*
    * we will expire their token 1 minute sooner than it actually expires,
    * to try to prevent weird behavior like a token expiring right before making an API call
    * expiresIn is stored as seconds, hence subtracting 60
    */
    const radix = 10;
    const expireBefore = 60;
    let expiresIn = parseInt(tokenResponse.data.expires_in, radix);
    if (expiresIn) {
      expiresIn -= expireBefore;
      const expirationDateTime = moment().add(expiresIn, 'seconds').format('YYYY-MM-DD HH:mm:ss');
      setAccessTokenExpiration(expirationDateTime);
    }

    if (accessToken) { setAccessToken(accessToken); }
    if (idToken) { setIdToken(idToken); }

    if (window.location.search && window.location.search.indexOf('code=') > 0) {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.pushState({}, document.title, url.href);
    }

    return accessToken;
  }
}

export function logout() {
  const storedProviderHost = getProviderHostUsedForAuth();
  
  let providerHost = storedProviderHost;
  let currentProviderHost = '';
  
  if (!storedProviderHost) {
    currentProviderHost = getProviderHost();
    providerHost = currentProviderHost;
  }
  
  let redirectUri = window.config.redirectUri; 
  if (window.location.origin.includes('clearinghouse')) {
    redirectUri = window.config.redirectUriEldch;
  } else if (window.location.origin.includes('platformscience')) {
    redirectUri = window.config.redirectUriPlatformScience;
  }
  
  const queryString = `?id_token_hint=${getIdToken()}&post_logout_redirect_uri=${redirectUri}`;
  const url = `https://${providerHost}logout${queryString}`;

  clearProviderHostUsedForAuth();
  
  window.location.assign(url);
}
